/**
 * Keyboard-open overlay handling for `AnimatedModalShell` (pure — no React /
 * no DOM, so it unit-tests in isolation without dragging the react-intl ESM
 * chain into the suite).
 *
 * SHAPE-BASED RULE (founder decision, 2026-08-20 — supersedes the earlier
 * fixed-box-only law the same day): the box NEVER animates its position
 * mid-keyboard-slide via padding/height tweening — that read as a "glitch"
 * on device (the instant-clamp build this replaced). Instead the shell picks
 * ONE of two native-feeling modes automatically, by measuring whether the
 * dialog's resting height fits above the keyboard:
 *
 *   • `'glide'` — the dialog FITS in the visible band. The whole box
 *     translates up ONCE, synced to the iOS keyboard curve (`KB_MOTION`) —
 *     this is how a native iOS alert/sheet that fits on screen behaves: it
 *     doesn't resize, it just glides clear of the keyboard.
 *   • `'curtain'` — the dialog is TALLER than the band. Its top edge pins in
 *     place (byte-identical to its resting position — invisible) and its
 *     bottom is clamped to the keyboard line, animated on the SAME curve so
 *     the edge visibly RIDES the keyboard's slide instead of snapping — this
 *     is how a native tall sheet behaves: it doesn't move, it just loses the
 *     room the keyboard took.
 *
 * The mode is decided automatically by measurement — never per call-site —
 * so it can't drift between modals the way a hand-picked prop would.
 *
 * The dim scrim STILL never sizes/positions itself off the visual-viewport
 * measurement in curtain mode (only `paddingTop`/`alignItems`) — on an
 * installed iOS PWA `window.visualViewport.height` reports stale/short
 * values after focus + scrollIntoView, and sizing the dim to it once leaked
 * the (scroll-locked but still painted) page below the modal — the "split
 * modal" report. Glide mode never touches the scrim at all (`scrim: null`).
 *
 * @param {{keyboardOpen:boolean, offsetTop:number, height:number, obscured:number}} viewportFrame
 * @param {{top:number, height:number}|null} restingRect — the dialog's measured
 *   resting position (offsetTop/offsetHeight — transform-immune — captured by
 *   the caller just before the keyboard opened). null while unmeasured yet
 *   (e.g. mid-morph).
 * @param {{2:string, 6:string}} sp — foundation.spacePx (px strings); kept in
 *   the signature for call-site compat, unused by this math.
 * @param {'center'|'end'} [align] — glide-mode target: `'center'` (default —
 *   every app modal) settles the dialog centered in the visible band;
 *   `'end'` hugs the keyboard (login cards, which look better close to the
 *   keys than floating mid-band).
 * @param {number} [topFloorPx] — glide top floor; defaults to `GLIDE_TOP_MIN_PX`,
 *   `useModalKeyboardMode` passes the live `glideTopFloorPx()`.
 * @returns {{mode: 'glide'|'curtain'|null, scrim: object|null, dialog: object|null}}
 */

import { predictKeyboardTop } from '../../utils/ui/inlineRevealPlan';
import { MODAL_TOP_RESERVE_VAR, MODAL_TOUCH_BAND_MAX_HEIGHT } from '../../ui/modalSizeTokens';

/** iOS keyboard slide timing (UIKit-like curve) — the glide/curtain motions track it. */
export const KB_MOTION = '250ms cubic-bezier(0.36, 0.66, 0.04, 1)';
/**
 * The pop-up LIFT at the tap (KAN-647): the card, its content flips and the
 * tucked step bar's followers. Softer and a touch longer than KB_MOTION — the
 * card is already on screen and moving, so ease-in-out. KB_MOTION's
 * front-loaded curve moved a short pop-up most of the way in one frame and
 * read as a snap. Bottom sheets and the glide back keep KB_MOTION.
 */
export const KB_LIFT_MS = 360;
export const KB_LIFT_MOTION = `${KB_LIFT_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
/** Transform-only list for nodes that move WITH the lifted card. */
export const KB_LIFT_TRANSITION = `transform ${KB_LIFT_MOTION}`;
/**
 * The ONE transition list every keyboard-motion writer on the DIALOG uses, so a
 * rewrite by one writer never drops (and cancels) another's running transition.
 */
export const KB_DIALOG_TRANSITION = `transform ${KB_LIFT_MOTION}, max-height ${KB_MOTION}, height ${KB_MOTION}`;

/**
 * Minimum band-top clearance for a glided dialog, so it never rides up under
 * the notch/Dynamic Island. `env(safe-area-inset-top)` isn't readable as a
 * number in pure JS (it's a CSS-only environment variable), so this is a
 * documented flat approximation rather than a live measurement.
 *
 * This flat value is only the FLOOR'S FLOOR (desktop / shell-less screens,
 * where `--cvx-modal-top-reserve` is unset). On the touch shell the live floor
 * is `glideTopFloorPx()` — the measured header reserve + the resting scrim's
 * 16px gap — so a lifted card never covers the app header.
 */
export const GLIDE_TOP_MIN_PX = 76;

/** Gap above the reserve; mirrors the resting scrim's `spacePx[4]` paddingTop. */
const TOP_RESERVE_GAP_PX = 16;

/**
 * Live top floor for a lifted/glided dialog: never above the app header.
 * Reads the numeric px of `--cvx-modal-top-reserve`; unset / unparseable / SSR
 * → 0, so the result is exactly `GLIDE_TOP_MIN_PX` (desktop unchanged).
 * @returns {number}
 */
export function glideTopFloorPx() {
  let reserve = 0;
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    try {
      // The touch shell writes the var inline on <html> (useModalTouchReserves).
      const raw = document.documentElement.style.getPropertyValue(MODAL_TOP_RESERVE_VAR);
      const n = parseFloat(raw);
      if (Number.isFinite(n) && n > 0) reserve = n;
    } catch { /* unreadable → no reserve */ }
  }
  return Math.max(GLIDE_TOP_MIN_PX, reserve + TOP_RESERVE_GAP_PX);
}

/** Gap kept between the dialog's bottom edge and the top of the keyboard. */
const BOTTOM_GAP_PX = 8;

/**
 * Would a dialog of this height still FIT above the predicted keyboard band
 * (glide), or would it enter curtain mode? Same inequality as
 * `computeKeyboardOverlayStyles`'s fits check, using `predictKeyboardTop`
 * for the band when the keyboard is not yet open.
 *
 * Anticipation (`useModalKeyboardAnticipation`) lifts any card EXCEPT a tall
 * one (does not fit) whose body scroller already overflows — those are
 * curtain-bound builders that pin their top and shrink maxHeight; lifting then
 * releasing them is the invoice/estimate up-then-down settle (2026-09-29).
 * A short card with an overflowing body still lifts (it fits).
 *
 * @param {number} dialogHeight
 * @param {number} [viewportHeight] — defaults to `window.innerHeight`
 * @param {number} [topFloorPx] — defaults to `GLIDE_TOP_MIN_PX`; the hook passes `glideTopFloorPx()`
 * @param {'numeric'|'text'} [kind] — the focused field's keyboard kind (omitted → tallest known)
 * @returns {boolean}
 */
export function dialogFitsAbovePredictedKeyboard(dialogHeight, viewportHeight, topFloorPx = GLIDE_TOP_MIN_PX, kind) {
  const vh = viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 0);
  if (!(dialogHeight > 0) || !(vh > 0)) return false;
  const kbTop = predictKeyboardTop(vh, kind);
  return dialogHeight <= kbTop - topFloorPx - BOTTOM_GAP_PX;
}


// eslint-disable-next-line no-unused-vars -- sp kept for call-site compat, see JSDoc
export function computeKeyboardOverlayStyles(viewportFrame, restingRect, sp, align = 'center', topFloorPx = GLIDE_TOP_MIN_PX) {
  if (!viewportFrame.keyboardOpen || restingRect == null) {
    return { mode: null, scrim: null, dialog: null };
  }

  const bandTop = viewportFrame.offsetTop;
  const bandBottom = viewportFrame.offsetTop + viewportFrame.height;
  const bandH = viewportFrame.height;

  const fits = restingRect.height <= (bandBottom - BOTTOM_GAP_PX) - (bandTop + topFloorPx);

  if (fits) {
    const targetTop = align === 'end'
      ? bandBottom - BOTTOM_GAP_PX - restingRect.height
      : Math.max(bandTop + topFloorPx, bandTop + (bandH - restingRect.height) / 2);
    let delta = Math.round(targetTop - restingRect.top);
    if (Math.abs(delta) < 2) delta = 0;
    // Center-align glides only ever move UP, out of the keyboard's way. A
    // DOWNWARD center-glide is pure re-centering aesthetics (the dialog
    // already clears the keyboard) and reads as unexplained drift — e.g. a
    // top-anchored typed-confirm card would slide down 20–60px as the
    // keyboard opened. `'end'` keeps its downward moves: hugging the keys IS
    // its purpose (login cards).
    if (align === 'center') delta = Math.min(delta, 0);
    return {
      mode: 'glide',
      scrim: null,
      // Transition is composed by the shell (it must stack with whatever
      // owns `transform` during enter/exit/morph) — see AnimatedModalShell.
      dialog: { transform: `translateY(${delta}px)` },
    };
  }

  // No-grow guard: Math.min(..., restingRect.height) means the clamp may only
  // SHRINK the dialog, never grow it past its natural resting height — the
  // caller's own maxHeight is fully replaced by ours below, so without this
  // guard a small keyboard could let content grow past the caller's cap.
  let clampPx = Math.min(bandBottom - BOTTOM_GAP_PX - restingRect.top, restingRect.height);
  // Absurd-clamp floor: guards against a stale/garbage measurement producing
  // an unusably short box — better a slightly-tall box than a sliver.
  clampPx = Math.max(clampPx, 120);
  return {
    mode: 'curtain',
    scrim: {
      // Pin the top edge at its resting position: flex-start + explicit
      // paddingTop replaces centering with the byte-identical visual
      // position the dialog already occupied. No paddingBottom/Left/Right,
      // no height/top/bottom — the resting `modalScrimCentered` values stay,
      // preserving the "dim scrim stays pinned full-viewport" invariant.
      alignItems: 'flex-start',
      paddingTop: `${restingRect.top}px`,
    },
    dialog: {
      maxHeight: `${clampPx}px`,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      // The bottom edge RIDES the keyboard's own slide: the SHELL owns the
      // dialog's `transition` (KB_DIALOG_TRANSITION + panel motion), so this
      // style carries none.
    },
  };
}

/** Transform-only member of the list — a measuring pass that must keep a running FLIP alive. */
export const KB_TRANSFORM_TRANSITION = `transform ${KB_MOTION}`;

/** Same rule for a bottom sheet (margin-bottom lifts it); compose with the sheet's own entries. */
export const KB_SHEET_TRANSITION = `transform ${KB_MOTION}, margin-bottom ${KB_MOTION}, max-height ${KB_MOTION}, height ${KB_MOTION}`;

/**
 * Static height cap for confirm cards that summon the keyboard on touch
 * devices: the card must FIT the band the keyboard leaves, so that when the
 * anticipation anchor lifts it at focus, glide mode applies and the card
 * REPOSITIONS WITHOUT RESIZING. Without this, a tall card (delete-client
 * with its impact list) entered curtain mode and visibly shrank at focus —
 * the founder's "it shrinks when I type" report (2026-08-21). Content
 * scrolls inside the card's body region instead (standard iOS tall-alert
 * behavior).
 */
export function summonDialogHeightCap() {
  if (typeof window === 'undefined') return undefined;
  const kbTop = predictKeyboardTop(window.innerHeight, 'text');
  const px = Math.max(240, kbTop - glideTopFloorPx() - BOTTOM_GAP_PX);
  return { maxHeight: `min(${px}px, ${MODAL_TOUCH_BAND_MAX_HEIGHT})` };
}
